import { useId, useState } from "react"
import { SpinnerText } from "./Spinner"

type DesignUploadBoxProps = {
  uploadedFiles: File[]
  onFilesSelect: (files: File[]) => void
  uploadStateByFile?: Record<
    string,
    {
      status: "uploading" | "uploaded" | "error"
      euipoDocumentId?: string
    }
  >
  disabled?: boolean
}

const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg"]
const MAX_FILE_SIZE_MB = 2
const MIN_IMAGE_WIDTH = 800
const MIN_IMAGE_HEIGHT = 800
const MAX_IMAGE_WIDTH = 5000
const MAX_IMAGE_HEIGHT = 5000
const MAX_IMAGE_VIEWS = 10

function getImageDimensions(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({ width: image.width, height: image.height })
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error("The image could not be read."))
    }

    image.src = objectUrl
  })
}

async function validateDesignFile(file: File) {
  const fileName = file.name.toLowerCase()
  const isAccepted = ACCEPTED_IMAGE_EXTENSIONS.some(extension =>
    fileName.endsWith(extension)
  )

  if (!isAccepted) {
    return "Allowed files: JPEG."
  }

  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `The file must not exceed ${MAX_FILE_SIZE_MB} MB.`
  }

  let width = 0
  let height = 0

  try {
    const dimensions = await getImageDimensions(file)
    width = dimensions.width
    height = dimensions.height
  } catch {
    return "The image could not be read."
  }

  if (width < MIN_IMAGE_WIDTH || height < MIN_IMAGE_HEIGHT) {
    return `The image must be at least ${MIN_IMAGE_WIDTH} x ${MIN_IMAGE_HEIGHT} pixels.`
  }

  if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT) {
    return `The image must not exceed ${MAX_IMAGE_WIDTH} x ${MAX_IMAGE_HEIGHT} pixels.`
  }

  return ""
}

function getDesignFileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`
}

function getFileKind(file: File) {
  const fileName = file.name.toLowerCase()
  if (ACCEPTED_IMAGE_EXTENSIONS.some(ext => fileName.endsWith(ext))) {
    return "image"
  }
  return "unknown"
}

export default function DesignUploadBox({
  uploadedFiles,
  onFilesSelect,
  uploadStateByFile = {},
  disabled = false
}: DesignUploadBoxProps) {
  const inputId = useId()
  const [error, setError] = useState("")

  const handleFiles = async (files: FileList | File[]) => {
    if (disabled) return

    const nextFiles = Array.from(files)
    const imageFiles = nextFiles.filter(file => getFileKind(file) === "image")
    const unknownFiles = nextFiles.filter(file => getFileKind(file) === "unknown")

    if (unknownFiles.length > 0) {
      setError("Allowed files: JPEG.")
      return
    }

    const existingKeys = new Set(uploadedFiles.map(getDesignFileKey))
    const uniqueImageFiles = imageFiles.filter(
      file => !existingKeys.has(getDesignFileKey(file))
    )
    const availableSlots = Math.max(0, MAX_IMAGE_VIEWS - uploadedFiles.length)
    const filesToAdd = uniqueImageFiles.slice(0, availableSlots)

    if (uniqueImageFiles.length > availableSlots) {
      setError(`You can upload up to ${MAX_IMAGE_VIEWS} design views.`)
      return
    }

    let validationError = ""

    for (const file of filesToAdd) {
      const result = await validateDesignFile(file)
      if (result) {
        validationError = result
        break
      }
    }

    setError(validationError)

    if (!validationError) {
      onFilesSelect([...uploadedFiles, ...filesToAdd])
    }
  }

  const removeFile = (indexToRemove: number) => {
    onFilesSelect(uploadedFiles.filter((_, index) => index !== indexToRemove))
  }

  return (
    <div className={disabled ? "opacity-50 pointer-events-none" : ""}>
      <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-sm text-gray-600 mb-4 cursor-pointer hover:border-orange-400 transition-colors bg-white">
        <input
          id={inputId}
          type="file"
          accept=".jpg,.jpeg"
          multiple
          disabled={disabled}
          onChange={(event) => {
            const files = event.target.files
            if (files) {
              void handleFiles(files)
            }
            event.target.value = ""
          }}
          className="hidden"
        />

        <label
          htmlFor={inputId}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            const files = event.dataTransfer.files
            if (files) {
              void handleFiles(files)
            }
          }}
          className="cursor-pointer"
        >
          <img src="/upload.png" alt="Upload icon" className="h-8 mx-auto mb-2 opacity-80" />

          <p className="text-gray-600">
            Drag your design views here or click here to upload them.
          </p>

          <p className="text-gray-600">
            JPEG up to {MAX_IMAGE_VIEWS} files.
          </p>

          <p className="text-xs text-gray-500 mt-2">
            JPEG, max. 2 MB, image size between 800 x 800 and 5,000 x 5,000 pixels.
          </p>
        </label>

        {error && (
          <p className="mt-3 text-sm text-red-500">{error}</p>
        )}

        {uploadedFiles.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-5 text-left">
            {uploadedFiles.length > 0 && (
              <div>
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h6 className="font-semibold text-blue-800">
                    Uploaded design views
                  </h6>

                  <button
                    type="button"
                    onClick={() => onFilesSelect([])}
                    className="text-sm font-semibold uppercase text-blue-800 hover:text-blue-700"
                  >
                    Delete all views
                  </button>
                </div>

                <div className="grid grid-cols-[repeat(auto-fill,144px)] gap-4">
                  {uploadedFiles.map((file, index) => {
                    const previewUrl = file.type.startsWith("image/")
                      ? URL.createObjectURL(file)
                      : null
                    const uploadState = uploadStateByFile[getDesignFileKey(file)]

                    return (
                      <div
                        key={`${file.name}-${index}`}
                        className="relative flex h-60 w-36 flex-col rounded bg-white p-3 text-center shadow-md"
                      >
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-500 text-xs font-semibold text-white hover:bg-gray-600"
                          aria-label="Remove design view"
                        >
                          x
                        </button>

                        <div className="flex h-36 items-center justify-center border-b border-gray-200 pb-3">
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={file.name}
                              className="max-h-32 max-w-full object-contain"
                            />
                          ) : (
                            <span className="text-sm font-semibold uppercase text-gray-500">
                              {file.name.split(".").pop()}
                            </span>
                          )}
                        </div>

                        <p className="mt-auto truncate pt-3 text-sm text-gray-600">
                          {index + 1} {file.name}
                        </p>

                        {uploadState?.status === "uploading" && (
                          <p className="mt-1 text-xs font-semibold text-gray-500">
                            <SpinnerText>Uploading...</SpinnerText>
                          </p>
                        )}

                        {uploadState?.status === "uploaded" && (
                          <p className="mt-1 truncate text-xs font-semibold text-green-700">
                            ID {uploadState.euipoDocumentId}
                          </p>
                        )}

                        {uploadState?.status === "error" && (
                          <p className="mt-1 text-xs font-semibold text-red-600">
                            Upload failed
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  )
}
